/**
 * Global skyAgent drawer (SkyCRM).
 *
 * Mounted once in AppLayout inside NotificationProvider, so skyAgent is
 * reachable from any CRM surface without each page wiring its own panel. The
 * context lets a future surface open it with a prospect in context ("ask
 * skyAgent about this record") without prop-drilling.
 *
 * A drawer is the correct overlay here by the design-system rule: it is a side
 * panel that PRESERVES the page context behind it, rather than a blocking
 * decision (dialog) or a trigger-anchored hint (popover). It is non-modal so the
 * operator can keep reading the record while the answer streams.
 *
 * STAFF-FACING ONLY (§1). This mounts inside the authenticated operator shell;
 * it is never rendered on a widget, ASA, or any visitor-facing surface.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { SkyDrawer } from "@skypointcloud/skypoint-ui";
import { ChatPanel } from "./ChatPanel";

interface SkyAgentDrawerContextValue {
  isOpen: boolean;
  /** Open the drawer, optionally scoping the conversation. */
  openSkyAgent: (context?: string) => void;
  closeSkyAgent: () => void;
}

const SkyAgentDrawerContext = createContext<SkyAgentDrawerContextValue | null>(null);

/** Read the drawer controls. Returns null outside the provider (safe no-op). */
export function useSkyAgentDrawer(): SkyAgentDrawerContextValue {
  const ctx = useContext(SkyAgentDrawerContext);
  return (
    ctx ?? {
      isOpen: false,
      openSkyAgent: () => undefined,
      closeSkyAgent: () => undefined,
    }
  );
}

export function SkyAgentDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<string | undefined>(undefined);

  const openSkyAgent = useCallback((next?: string) => {
    setContext(next);
    setIsOpen(true);
  }, []);
  const closeSkyAgent = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, openSkyAgent, closeSkyAgent }),
    [isOpen, openSkyAgent, closeSkyAgent],
  );

  return (
    <SkyAgentDrawerContext.Provider value={value}>
      {children}
      <SkyDrawer
        open={isOpen}
        onOpenChange={setIsOpen}
        position="end"
        customSize="520px"
        headerTitle="skyAgent"
        subtitle="Answers from your CRM, scoped to what you can see"
        modal={false}
      >
        {/*
          The drawer is narrow, so the history rail is suppressed here; the full
          page surface (/crm/skyagent) carries it.
        */}
        <div className="h-full min-h-0">
          <ChatPanel
            context={context}
            showHistoryRail={false}
          />
        </div>
      </SkyDrawer>
    </SkyAgentDrawerContext.Provider>
  );
}

export default SkyAgentDrawerProvider;
